/**
 * GlobalSearch — współdzielony „środek" globalnej wyszukiwarki: pole + lista wyników.
 * Ten sam komponent obsługuje DWA scope'y (tenant / admin ITE) i DWA warianty layoutu:
 *   - `layout="inline"` — pole w górnym pasku; wyniki jako DROPDOWN pod polem,
 *   - `layout="modal"`  — pole + wyniki w oknie (Ctrl+K, przez GlobalSearchModal).
 *   - `scope="tenant"` (domyślny) — kartoteki/dokumenty tenanta (`/api/search`),
 *   - `scope="admin"`  — encje platformowe panelu ITE (`/api/admin/search`).
 *
 * Grupy z bazy liczy backend (per scope); grupa „Pozycje menu" składana tu ze
 * statycznych tras SPA. Nawigacja przez `navigate()` (zmiana location; TabContext
 * podłapuje i otwiera kartę — jak NavLink w sidebarze).
 *
 * Klawiatura: ↑/↓ nawigacja, Enter otwiera zaznaczony, Esc czyści/zamyka dropdown (inline).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ADMIN_SYSTEM_ITEMS,
  BASE_PACKAGE_KARTOTEKI,
  DOCUMENT_KARTOTEKI,
  SYSTEM_ITEMS,
} from '@saas/shared';
import {
  ArrowRight,
  BadgeCheck,
  Briefcase,
  Building2,
  ClipboardList,
  CornerDownLeft,
  FileText,
  LayoutGrid,
  type LucideIcon,
  Package,
  Receipt,
  Search,
  Settings,
  ShieldCheck,
  Ticket,
  UserCog,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useTenantMenuLabelMap } from '@/components/Sidebar';
import { useTenantAuthOptional } from '@/contexts/TenantAuthContext';
import { settingsSectionAccessFor } from '@/hooks/useSettingsSectionAccess';
import { visibleSettingsSearchEntries } from '@/lib/settings-search-entries';
import { ADMIN_MIN_SEARCH_LEN, useAdminGlobalSearch } from '@/lib/use-admin-global-search';
import { type DocumentClass, MIN_SEARCH_LEN, useGlobalSearch } from '@/lib/use-global-search';

export type GlobalSearchLayout = 'inline' | 'modal';
export type GlobalSearchScope = 'tenant' | 'admin';

interface GlobalSearchProps {
  layout: GlobalSearchLayout;
  /** Scope danych — 'tenant' (domyślny) lub 'admin' (panel ITE). */
  scope?: GlobalSearchScope;
  /** Wołane po otwarciu wyniku (modal → zamknij okno). */
  onNavigated?: () => void;
  /** Autofokus pola — modal ustawia true. */
  autoFocus?: boolean;
}

/** Płaski wiersz wyniku — wspólny kształt dla grup z bazy i nawigacyjnych. */
interface FlatItem {
  key: string;
  groupLabel: string;
  title: string;
  subtitle: string | null;
  path: string;
  icon: LucideIcon;
  typeLabel: string | null;
}

/** Klasa dokumentu → definicja kartoteki (trasa + etykieta) z jedynego źródła prawdy. */
const DOC_BY_CLASS = new Map(DOCUMENT_KARTOTEKI.map((d) => [d.documentClass, d]));

/** Zadania — trasa i etykieta kartoteki z jedynego źródła prawdy (`BASE_PACKAGE_KARTOTEKI`). */
const TASKS_KARTOTEKA = BASE_PACKAGE_KARTOTEKI.find((k) => k.slug === 'tasks');

/** Normalizacja do porównań: lowercase + zdjęcie polskich znaków diakrytycznych. */
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Podświetlenie dopasowanych słów frazy w tytule. */
function highlight(text: string, words: string[]): React.ReactNode {
  if (words.length === 0) return text;
  const pattern = words
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .filter(Boolean)
    .join('|');
  if (!pattern) return text;
  const parts = text.split(new RegExp(`(${pattern})`, 'gi'));
  return parts.map((part, i) =>
    words.some((w) => normalize(w) === normalize(part)) ? (
      <mark
        key={i}
        className="rounded-sm bg-amber-100 px-0.5 text-amber-900 dark:bg-amber-500/25 dark:text-amber-200"
      >
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export function GlobalSearch({
  layout,
  scope = 'tenant',
  onNavigated,
  autoFocus,
}: GlobalSearchProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = scope === 'admin';
  const ns = isAdmin ? 'admin.search' : 'tenant.search';
  const minLen = isAdmin ? ADMIN_MIN_SEARCH_LEN : MIN_SEARCH_LEN;

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounce frazy (200 ms).
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Oba hooki wołane zawsze (reguła hooków); aktywny tylko ten z bieżącego scope'u.
  const tenantQuery = useGlobalSearch(debounced, { enabled: !isAdmin });
  const adminQuery = useAdminGlobalSearch(debounced, { enabled: isAdmin });
  const isFetching = isAdmin ? adminQuery.isFetching : tenantQuery.isFetching;

  const words = useMemo(() => debounced.split(/\s+/).filter((w) => w.length > 0), [debounced]);

  // Tenant: PRAWDZIWE menu tenanta (moduły + własny layout z Kreatora — te same etykiety co lewy
  // panel) i sekcje Ustawień wg uprawnień. Kontekst opcjonalny — panel ITE nie ma sesji tenanta.
  const tenantAuth = useTenantAuthOptional();
  const tenantNav = !isAdmin && tenantAuth !== null;
  const tenantMe = tenantAuth?.me;
  const menuLabels = useTenantMenuLabelMap(tenantNav);
  const settingsEntries = useMemo(
    () => (tenantNav ? visibleSettingsSearchEntries(settingsSectionAccessFor(tenantMe)) : []),
    [tenantNav, tenantMe],
  );

  // Grupa nawigacyjna (statyczne trasy SPA) — filtrowana po stronie klienta.
  const navMenu = useMemo(() => {
    if (debounced.length < minLen) return [];
    const normWords = words.map(normalize);
    const matches = (label: string) => {
      const n = normalize(label);
      return normWords.every((w) => n.includes(w));
    };
    const entries = isAdmin
      ? ADMIN_SYSTEM_ITEMS.map((s) => ({ label: t(s.labelI18nKey, s.defaultLabel), path: s.path }))
      : [
          ...SYSTEM_ITEMS.map((s) => ({ label: t(s.labelI18nKey, s.defaultLabel), path: s.path })),
          ...BASE_PACKAGE_KARTOTEKI.map((k) => ({ label: k.label, path: k.path })),
          ...[...menuLabels].map(([path, label]) => ({ label, path })),
        ];
    // Ta sama trasa z kilku źródeł (np. kartoteka bazowa + pozycja menu) → jeden wynik.
    const seen = new Set<string>();
    return entries.filter((e) => {
      if (!matches(e.label) || seen.has(e.path)) return false;
      seen.add(e.path);
      return true;
    });
  }, [debounced, words, t, isAdmin, minLen, menuLabels]);

  // Grupa „Ustawienia" — tylko tenant: sekcje Ustawień, które user widzi (Kreator menu, Słowniki,
  // Centrum uprawnień…). Dopasowanie po „Ustawienia <sekcja>", więc samo „ustawienia" pokazuje
  // pełną listę. Bez sesji tenanta zostaje stary zestaw (Ustawienia + Profil).
  const navSettings = useMemo(() => {
    if (isAdmin || debounced.length < minLen) return [];
    const normWords = words.map(normalize);
    const settingsLabel = t('tenant.sidebar.settings', 'Ustawienia');
    const matches = (label: string) =>
      normWords.every((w) => normalize(`${settingsLabel} ${label}`).includes(w));
    const sections =
      settingsEntries.length > 0
        ? settingsEntries.map((e) => ({ label: t(e.labelKey), path: e.path }))
        : [{ label: settingsLabel, path: '/settings' }];
    // Trasa już obecna w „Pozycjach menu" (np. /settings z paska) — bez dubla.
    const inMenu = new Set(navMenu.map((m) => m.path));
    return [
      ...sections,
      { label: t('tenant.settings.tabs.profile', 'Profil'), path: '/profile' },
    ].filter((e) => matches(e.label) && !inMenu.has(e.path));
  }, [isAdmin, debounced, words, t, minLen, settingsEntries, navMenu]);

  // Spłaszczona lista wyników (kolejność grup wg makiety) — do nawigacji klawiaturą.
  const flat = useMemo<FlatItem[]>(() => {
    const items: FlatItem[] = [];

    if (isAdmin) {
      const r = adminQuery.data?.results;
      for (const x of r?.tenants ?? []) {
        items.push({
          key: `tenant:${x.id}`,
          groupLabel: t('admin.search.groups.tenants'),
          title: x.title,
          subtitle: x.subtitle,
          path: `/tenants/${x.id}`,
          icon: Building2,
          typeLabel: t('admin.search.types.tenant'),
        });
      }
      for (const x of r?.adminUsers ?? []) {
        items.push({
          key: `adminUser:${x.id}`,
          groupLabel: t('admin.search.groups.adminUsers'),
          title: x.title,
          subtitle: x.subtitle,
          // Brak trasy detalu — lista z filtrem po frazie.
          path: `/admin-users?search=${encodeURIComponent(x.title)}`,
          icon: ShieldCheck,
          typeLabel: t('admin.search.types.adminUser'),
        });
      }
      for (const x of r?.invoices ?? []) {
        items.push({
          key: `invoice:${x.id}`,
          groupLabel: t('admin.search.groups.invoices'),
          title: x.title,
          subtitle: x.subtitle,
          path: `/invoices?search=${encodeURIComponent(x.title)}`,
          icon: Receipt,
          typeLabel: t('admin.search.types.invoice'),
        });
      }
      for (const x of r?.coupons ?? []) {
        items.push({
          key: `coupon:${x.id}`,
          groupLabel: t('admin.search.groups.coupons'),
          title: x.title,
          subtitle: x.subtitle,
          path: `/coupons?search=${encodeURIComponent(x.title)}`,
          icon: Ticket,
          typeLabel: t('admin.search.types.coupon'),
        });
      }
      for (const x of r?.modules ?? []) {
        items.push({
          key: `module:${x.id}`,
          groupLabel: t('admin.search.groups.modules'),
          title: x.title,
          subtitle: x.subtitle,
          path: `/modules?search=${encodeURIComponent(x.subtitle ?? x.title)}`,
          icon: Package,
          typeLabel: t('admin.search.types.module'),
        });
      }
    } else {
      const r = tenantQuery.data?.results;
      for (const c of r?.contractors ?? []) {
        items.push({
          key: `contractor:${c.id}`,
          groupLabel: t('tenant.search.groups.contractors'),
          title: c.title,
          subtitle: c.subtitle,
          path: `/contractors/${c.id}`,
          icon: Briefcase,
          typeLabel: t('tenant.search.types.contractor'),
        });
      }
      for (const e of r?.employees ?? []) {
        items.push({
          key: `employee:${e.id}`,
          groupLabel: t('tenant.search.groups.employees'),
          title: e.title,
          subtitle: e.subtitle,
          path: `/employees/${e.id}`,
          icon: BadgeCheck,
          typeLabel: t('tenant.search.types.employee'),
        });
      }
      for (const u of r?.users ?? []) {
        items.push({
          key: `user:${u.id}`,
          groupLabel: t('tenant.search.groups.users'),
          title: u.title,
          subtitle: u.subtitle,
          path: `/users/${u.id}`,
          icon: UserCog,
          typeLabel: t('tenant.search.types.user'),
        });
      }
      for (const d of r?.documents ?? []) {
        const def = DOC_BY_CLASS.get(d.documentClass as DocumentClass);
        items.push({
          key: `document:${d.id}`,
          groupLabel: t('tenant.search.groups.documents'),
          title: d.title,
          subtitle: d.subtitle,
          path: def ? `${def.path}/${d.id}` : `/documents/${d.id}`,
          icon: FileText,
          typeLabel: def?.label ?? t('tenant.search.types.document'),
        });
      }
      for (const k of r?.tasks ?? []) {
        items.push({
          key: `task:${k.id}`,
          groupLabel: t('tenant.search.groups.tasks'),
          title: k.title,
          subtitle: k.subtitle,
          path: `${TASKS_KARTOTEKA?.path ?? '/tasks'}/${k.id}`,
          icon: ClipboardList,
          typeLabel: t('tenant.search.types.task'),
        });
      }
    }

    for (const m of navMenu) {
      items.push({
        key: `menu:${m.path}`,
        groupLabel: t(`${ns}.groups.menu`),
        title: m.label,
        subtitle: null,
        path: m.path,
        icon: LayoutGrid,
        typeLabel: t(`${ns}.types.menu`),
      });
    }
    for (const s of navSettings) {
      items.push({
        key: `settings:${s.path}`,
        groupLabel: t('tenant.search.groups.settings'),
        title: s.label,
        subtitle: null,
        path: s.path,
        icon: Settings,
        typeLabel: t('tenant.search.types.settings'),
      });
    }
    return items;
  }, [isAdmin, adminQuery.data, tenantQuery.data, navMenu, navSettings, ns, t]);

  useEffect(() => {
    setActiveIndex(0);
  }, [flat.length]);

  // Inline: klik poza komponentem zamyka dropdown.
  useEffect(() => {
    if (layout !== 'inline' || !dropdownOpen) return;
    const handler = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [layout, dropdownOpen]);

  const go = useCallback(
    (item: FlatItem | undefined) => {
      if (!item) return;
      navigate(item.path);
      setQuery('');
      setDebounced('');
      setDropdownOpen(false);
      onNavigated?.();
    },
    [navigate, onNavigated],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && layout === 'inline' && dropdownOpen) {
      e.stopPropagation();
      // Esc zamyka listę wyników — „obsłużone", okno nadrzędne (jeśli jest) się nie zamyka.
      e.preventDefault();
      setDropdownOpen(false);
      return;
    }
    if (flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[activeIndex]);
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const showHint = debounced.length < minLen;
  const showEmpty = debounced.length >= minLen && !isFetching && flat.length === 0;

  // ── Lista wyników — TEN SAM markup w dropdownie i w modalu ──────────────────
  let lastGroup: string | null = null;
  const resultsBody = (
    <>
      <div ref={listRef} className="max-h-[52vh] overflow-y-auto">
        {showHint && (
          <p className="px-2 py-8 text-center text-sm text-text-muted">{t(`${ns}.hint`)}</p>
        )}
        {isFetching && flat.length === 0 && !showHint && (
          <p className="px-2 py-8 text-center text-sm text-text-muted">{t(`${ns}.loading`)}</p>
        )}
        {showEmpty && (
          <p
            className="px-2 py-8 text-center text-sm text-text-muted"
            data-testid="global-search-empty"
          >
            {t(`${ns}.empty`, { q: debounced })}
          </p>
        )}

        {flat.map((item, idx) => {
          const header = item.groupLabel !== lastGroup ? item.groupLabel : null;
          lastGroup = item.groupLabel;
          const Icon = item.icon;
          const active = idx === activeIndex;
          return (
            <div key={item.key}>
              {header && (
                <div className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  {header}
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                data-index={idx}
                data-testid="global-search-result"
                onClick={() => go(item)}
                onMouseEnter={() => setActiveIndex(idx)}
                leftIcon={
                  <Icon
                    size={17}
                    aria-hidden="true"
                    className={active ? 'text-accent-600 dark:text-accent-300' : 'text-text-muted'}
                  />
                }
                className={[
                  'w-full justify-start gap-3 px-2 py-2 text-left font-normal',
                  active ? 'bg-accent-50 dark:bg-accent-500/15' : 'hover:bg-bg-secondary',
                ].join(' ')}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-text-primary">
                    {highlight(item.title, words)}
                  </span>
                  {item.subtitle && (
                    <span className="block truncate text-xs text-text-secondary">
                      {item.subtitle}
                    </span>
                  )}
                </span>
                {active ? (
                  <span className="flex shrink-0 items-center gap-1 text-[11px] text-accent-600 dark:text-accent-300">
                    <CornerDownLeft size={12} aria-hidden="true" />
                    {t(`${ns}.open`)}
                  </span>
                ) : (
                  item.typeLabel && (
                    <span className="shrink-0 text-[11px] text-text-muted">{item.typeLabel}</span>
                  )
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {flat.length > 0 && (
        <div className="mt-2 flex items-center gap-4 border-t border-border-secondary px-2 pt-2 text-[11px] text-text-muted">
          <span className="flex items-center gap-1">
            <span className="rounded border border-border-secondary px-1">↑↓</span>
            {t(`${ns}.footer.navigate`)}
          </span>
          <span className="flex items-center gap-1">
            <span className="rounded border border-border-secondary px-1">↵</span>
            {t(`${ns}.footer.open`)}
          </span>
          <span className="flex items-center gap-1">
            <span className="rounded border border-border-secondary px-1">Esc</span>
            {t(`${ns}.footer.close`)}
          </span>
          <span className="ml-auto flex items-center gap-1">
            {t(`${ns}.footer.count`, { count: flat.length })}
            <ArrowRight size={11} aria-hidden="true" />
          </span>
        </div>
      )}
    </>
  );

  const inputEl = (
    <Input
      autoFocus={autoFocus}
      type="text"
      value={query}
      onChange={(e) => {
        setQuery(e.target.value);
        if (layout === 'inline') setDropdownOpen(true);
      }}
      onFocus={() => {
        if (layout === 'inline' && query.trim().length > 0) setDropdownOpen(true);
      }}
      onKeyDown={onKeyDown}
      placeholder={layout === 'inline' ? t(`${ns}.trigger`) : t(`${ns}.placeholder`)}
      prefixIcon={<Search size={layout === 'inline' ? 18 : 16} aria-hidden="true" />}
      // Pole inline wg Figmy „Aura / Search field" (123:24680): wysokość 44, radius pełny (999px),
      // border #c3c3c7, delikatny cień. Modal zostaje przy domyślnym prymitywie.
      className={
        layout === 'inline'
          ? 'h-11 !rounded-full border-[#c3c3c7] shadow-[0_1px_1px_rgba(23,23,33,0.06)]'
          : undefined
      }
      // Podpowiedź skrótu WEWNĄTRZ pola (wg Figmy) — filled chip #f1f1f5, tylko wariant inline.
      suffixIcon={
        layout === 'inline' ? (
          <span className="hidden items-center rounded-[10px] bg-bg-tertiary px-2 py-1 text-[12px] font-medium leading-none text-text-tertiary sm:inline-flex">
            Ctrl K
          </span>
        ) : undefined
      }
      containerClassName="flex-1"
      aria-label={t(`${ns}.placeholder`)}
      data-testid="global-search-input"
    />
  );

  // ── Wariant MODAL ──────────────────────────────────────────────────────────
  if (layout === 'modal') {
    return (
      <div className="-mx-1 -my-1" data-testid="global-search">
        <div className="flex items-center gap-2 border-b border-border-secondary pb-3">
          {inputEl}
          <span className="hidden shrink-0 rounded border border-border-secondary px-1.5 py-0.5 text-[11px] text-text-muted sm:inline">
            Ctrl K
          </span>
        </div>
        <div className="mt-2">{resultsBody}</div>
      </div>
    );
  }

  // ── Wariant INLINE ─────────────────────────────────────────────────────────
  const inlineOpen = dropdownOpen && query.trim().length > 0;
  return (
    <div ref={rootRef} className="relative w-full" data-testid="global-search">
      {/* „Ctrl K" jest teraz WEWNĄTRZ pola (suffixIcon inputa), nie obok. */}
      <div className="flex items-center gap-2">{inputEl}</div>
      {inlineOpen && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-border-secondary bg-bg-elevated p-2 shadow-[0_18px_50px_rgba(31,41,55,0.28)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.6)]"
          data-testid="global-search-dropdown"
        >
          {resultsBody}
        </div>
      )}
    </div>
  );
}
